import React, { useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { motion } from "framer-motion";
import {
  FaStar,
  FaUsers,
  FaAward,
  FaComments,
  FaWhatsapp,
  FaPhoneAlt,
  FaArrowDown,
  FaTimes,
} from "react-icons/fa";
import Button from "../common/Button";
import ContactForm from "../forms/ContactForm";
import styles from "./Hero.module.css";

const Hero = ({ onScrollToServices }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const stats = [
    { number: "15+", label: "Years Experience", icon: FaStar },
    { number: "10k+", label: "Patients Served", icon: FaUsers },
    { number: "20%", label: "EHR Error Reduction", icon: FaAward },
    { number: "24/7", label: "Telemedicine Support", icon: FaComments },
  ];

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    fade: true,
    arrows: true,
    pauseOnHover: false,
    cssEase: "ease-in-out",
    appendDots: (dots) => (
      <div>
        <ul className={styles.dots}> {dots} </ul>
      </div>
    ),
    customPaging: (i) => <div className={styles.dot}></div>,
  };

  const slides = [
    {
      image: "/assets/hero-expert-care.jpg",
      title: "Expert Care, Modern Approach",
      subtitle:
        "Your health is our top priority. Experience compassionate medical services.",
    },
    {
      image: "/assets/telemedicine.jpg",
      title: "Telemedicine Available",
      subtitle:
        "Consult with Dr. Jack Kimeu from anywhere via WhatsApp, phone, or chat.",
    },
    {
      image: "/assets/hero-medical-services.jpg",
      title: "Comprehensive Services",
      subtitle:
        "From diagnostics to rehabilitation – we cover all your healthcare needs.",
    },
  ];

  const floatingVariants = {
    animate: {
      y: [0, -20, 0],
      rotate: [0, 180, 360],
      transition: { duration: 6, repeat: Infinity, ease: "easeInOut" },
    },
  };

  return (
    <>
      <div className={styles.heroWrapper}>
        {/* Animated Background Elements */}
        <div className={styles.heroBackground}>
          <motion.div
            className={styles.floatingCircle}
            variants={floatingVariants}
            animate="animate"
            style={{ top: "10%", left: "5%", width: "100px", height: "100px" }}
          />
          <motion.div
            className={styles.floatingCircle}
            variants={floatingVariants}
            animate="animate"
            style={{
              bottom: "15%",
              right: "8%",
              width: "150px",
              height: "150px",
              animationDelay: "2s",
            }}
          />
          <motion.div
            className={styles.floatingCircle}
            variants={floatingVariants}
            animate="animate"
            style={{
              top: "60%",
              left: "15%",
              width: "80px",
              height: "80px",
              animationDelay: "4s",
            }}
          />
        </div>

        {/* Hero Slider */}
        <div className={styles.heroSlider}>
          <Slider {...sliderSettings}>
            {slides.map((slide, idx) => (
              <div key={idx} className={styles.slide}>
                <div
                  className={styles.slideImage}
                  style={{ backgroundImage: `url(${slide.image})` }}
                >
                  <div className={styles.overlay}>
                    <div className="container">
                      <div className={styles.content}>
                        <h1 className={styles.title}>
                          <span className={styles.gradientText}>
                            {slide.title}
                          </span>
                        </h1>
                        <p className={styles.subtitle}>{slide.subtitle}</p>
                        <div className={styles.buttons}>
                          <Button
                            href="https://wa.me/254722265791?text=Hello,%20I%20would%20like%20an%20online%20consultation"
                            variant="whatsapp"
                            className={styles.pulseButton}
                          >
                            <FaWhatsapp /> WhatsApp Consultation
                          </Button>
                          <Button href="tel:+254722265791" variant="secondary">
                            <FaPhoneAlt /> Call Now
                          </Button>
                          <Button
                            variant="primary"
                            onClick={() => setIsModalOpen(true)}
                          >
                            Book Appointment
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>

        {/* Stats Section */}
        <div className={styles.statsSection}>
          <div className="container">
            <div className={styles.statsGrid}>
              {stats.map((stat, idx) => (
                <motion.div
                  key={idx}
                  className={styles.statCard}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className={styles.statIcon}>
                    <stat.icon />
                  </div>
                  <div className={styles.statNumber}>{stat.number}</div>
                  <div className={styles.statLabel}>{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll Down Indicator */}
        <button
          className={styles.scrollIndicator}
          onClick={onScrollToServices}
          aria-label="Scroll to services"
        >
          <span className={styles.scrollText}>Discover Our Services</span>
          <motion.div
            className={styles.scrollArrow}
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <FaArrowDown />
          </motion.div>
        </button>
      </div>

      {/* Modal for Appointment Booking */}
      {isModalOpen && (
        <div
          className={styles.modalOverlay}
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.modalClose}
              onClick={() => setIsModalOpen(false)}
            >
              <FaTimes />
            </button>
            <h2 className={styles.modalTitle}>Book an Appointment</h2>
            <ContactForm />
          </div>
        </div>
      )}
    </>
  );
};

export default Hero;
